import * as htmlToImage from "html-to-image";
import { jsPDF } from "jspdf";

async function nodeToCanvas(node, scale = 2.5) {
  // Ensure web fonts are fully loaded before rasterizing, otherwise the
  // renderer falls back to fonts with different metrics, which collapses
  // spacing (e.g. "Congratulations" overlapping the CLASS TOPPERS banner) or
  // captures blank/garbled Indic glyphs.
  if (document.fonts && document.fonts.ready) {
    const fonts = [
      "400 16px 'Noto Sans Kannada'",
      "700 16px 'Noto Sans Kannada'",
      "400 16px 'Noto Serif Kannada'",
      "700 30px 'Dancing Script'",
      "600 16px 'Montserrat'",
      "700 16px 'Montserrat'",
      "800 42px 'Montserrat'",
      "900 38px 'Merriweather'",
      "700 40px 'Baloo 2'",
      "800 76px 'Baloo 2'",
    ];
    try {
      await Promise.all(fonts.map((f) => document.fonts.load(f)));
    } catch (_) { /* ignore individual font load errors */ }
    await document.fonts.ready;
  }
  // html-to-image renders via SVG <foreignObject>, i.e. the browser's own
  // engine — text baselines are pixel-perfect. (html2canvas repainted glyphs
  // ~10-15px lower than the browser, so downloaded posters had text sitting
  // low / off-centre inside boxes and the footer line clipped.)
  return htmlToImage.toCanvas(node, {
    pixelRatio: scale,
    backgroundColor: "#ffffff",
    width: node.offsetWidth,
    height: node.offsetHeight,
  });
}

function triggerDownload(dataUrl, filename) {
  // Chrome silently drops anchor downloads whose data: URL exceeds ~2MB —
  // large posters (e.g. Birthday with full-bleed confetti) hit this. Convert
  // to a Blob object URL, which has no size limit.
  const file = dataUrlToFile(dataUrl, filename);
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const safe = (name) => (name || "announcement").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 60);

// Wait until every <img> inside the node has finished loading (or errored) so
// html2canvas never rasterizes a half-loaded student photo.
export async function waitForImages(node) {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });
  }));
}

// Render a node to a data URL (used for publishing/sharing without a download).
export async function exportDataUrl(node, format = "jpeg", scale = 2.5) {
  await waitForImages(node);
  const canvas = await nodeToCanvas(node, scale);
  const mime = format === "png" ? "image/png" : "image/jpeg";
  return canvas.toDataURL(mime, 0.92);
}

function dataUrlToFile(dataUrl, filename) {
  const [meta, b64] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(meta)[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

// Native share (mobile / https): shares the poster PNG as a file if supported.
export async function shareNode(node, title) {
  const dataUrl = await exportDataUrl(node, "png", 3);
  const file = dataUrlToFile(dataUrl, `${safe(title)}.png`);
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title, text: title });
    return "shared";
  }
  return "unsupported";
}

// Copy the poster image to the clipboard (PNG).
export async function copyNodeImage(node) {
  const dataUrl = await exportDataUrl(node, "png", 3);
  const file = dataUrlToFile(dataUrl, "poster.png");
  if (navigator.clipboard && window.ClipboardItem) {
    await navigator.clipboard.write([new window.ClipboardItem({ "image/png": file })]);
    return "copied";
  }
  return "unsupported";
}

export async function exportImage(node, title, format = "png") {
  await waitForImages(node);
  const canvas = await nodeToCanvas(node, 3);
  const mime = format === "jpeg" ? "image/jpeg" : "image/png";
  const dataUrl = canvas.toDataURL(mime, 0.95);
  triggerDownload(dataUrl, `${safe(title)}.${format === "jpeg" ? "jpg" : "png"}`);
}

export async function exportPdf(node, title, orientation = "portrait") {
  await waitForImages(node);
  const canvas = await nodeToCanvas(node, 2.5);
  const imgData = canvas.toDataURL("image/jpeg", 0.95);
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW;
  const imgH = (canvas.height * imgW) / canvas.width;

  if (imgH <= pageH + 2) {
    // Fits one page (±2pt tolerance avoids a blank second page from rounding).
    pdf.addImage(imgData, "JPEG", 0, 0, imgW, Math.min(imgH, pageH));
  } else {
    // Slice across multiple A4 pages for long content.
    let remaining = imgH;
    let position = 0;
    while (remaining > 0) {
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      remaining -= pageH;
      if (remaining > 0) {
        pdf.addPage();
        position -= pageH;
      }
    }
  }
  pdf.save(`${safe(title)}.pdf`);
}
